import { defaultReadingPreferences, type LocatorJson, type ReadingPreferences } from "@msb/reader-bridge";
import { libraryFilters, librarySorts, type LibraryFilter, type LibrarySort } from "./shop/librarySort.ts";

// Préférences, dernière position et signets conservés dans le navigateur : la copie de travail, qui marche hors
// ligne. Avec un compte connecté, sync.ts les envoie au serveur IA et reçoit ceux des autres appareils. Jamais en mode extrait.

const PREFS_KEY = "msb-reader:preferences";
// Clé stable par livre : l'URL du manifest contient un jeton qui change à chaque ouverture.
const locationKey = (bookId: string) => `msb-reader:location:${bookId}`;

function read<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Stockage indisponible (navigation privée, quota) : on continue sans persistance.
  }
}

export function loadPreferences(): ReadingPreferences {
  return { ...defaultReadingPreferences, ...read<Partial<ReadingPreferences>>(PREFS_KEY) };
}

export function savePreferences(prefs: ReadingPreferences): void {
  write(PREFS_KEY, prefs);
}

export function loadLocation(bookId: string): LocatorJson | undefined {
  return read<LocatorJson>(locationKey(bookId));
}

// Quand cet appareil a noté la position : sert à savoir si celle d'un autre appareil est plus récente.
const locationTimeKey = (bookId: string) => `msb-reader:location-at:${bookId}`;

export function loadLocationTime(bookId: string): number {
  const value = read<number>(locationTimeKey(bookId));
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function saveLocation(bookId: string, locator: LocatorJson): void {
  write(locationKey(bookId), locator);
  write(locationTimeKey(bookId), Date.now());
}

/*
 * Progression de lecture (0 à 1) par livre de la bibliothèque, affichée sous la couverture.
 * Clé : identifiant du livre dans la boutique, seul connu de la bibliothèque.
 */
const progressKey = (libraryBookId: string) => `msb-reader:progress:${libraryBookId}`;

export function loadProgress(libraryBookId: string): number | undefined {
  const value = read<number>(progressKey(libraryBookId));
  return typeof value === "number" && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : undefined;
}

export function saveProgress(libraryBookId: string, progress: number): void {
  if (Number.isFinite(progress)) write(progressKey(libraryBookId), Math.min(1, Math.max(0, progress)));
}

/** Affichage de la bibliothèque, comme dans l'app : grille de couvertures ou liste. */
export type LibraryView = "grid" | "list";
const LIBRARY_VIEW_KEY = "msb-reader:library-view";

export function loadLibraryView(): LibraryView {
  return read<LibraryView>(LIBRARY_VIEW_KEY) === "list" ? "list" : "grid";
}

export function saveLibraryView(view: LibraryView): void {
  write(LIBRARY_VIEW_KEY, view);
}

/** Tri et filtre de la bibliothèque, gardés d'une visite à l'autre. */
export interface LibraryOrder {
  sort: LibrarySort;
  filter: LibraryFilter;
}
const LIBRARY_ORDER_KEY = "msb-reader:library-order";

export function loadLibraryOrder(): LibraryOrder {
  const saved = read<Partial<LibraryOrder>>(LIBRARY_ORDER_KEY);
  return {
    sort: librarySorts.includes(saved?.sort as LibrarySort) ? saved!.sort! : "recent",
    filter: libraryFilters.includes(saved?.filter as LibraryFilter) ? saved!.filter! : "all",
  };
}

export function saveLibraryOrder(order: LibraryOrder): void {
  write(LIBRARY_ORDER_KEY, order);
}

export interface Bookmark {
  id: string;
  locator: LocatorJson;
  createdAt: string;
}

const bookmarksKey = (bookId: string) => `msb-reader:bookmarks:${bookId}`;

export function loadBookmarks(bookId: string): Bookmark[] {
  return read<Bookmark[]>(bookmarksKey(bookId)) ?? [];
}

export function saveBookmarks(bookId: string, bookmarks: Bookmark[]): void {
  write(bookmarksKey(bookId), bookmarks);
}

/** Disposition choisie : part du volet de lecture et chat ouvert ou non (écrans larges). */
export interface LayoutSettings {
  readerShare: number;
  chatOpen: boolean;
}

const LAYOUT_KEY = "msb-reader:layout";
export const MIN_READER_SHARE = 0.35;
export const MAX_READER_SHARE = 0.75;

export function clampShare(value: number): number {
  return Math.min(MAX_READER_SHARE, Math.max(MIN_READER_SHARE, value));
}

export function loadLayout(): LayoutSettings {
  const saved = read<Partial<LayoutSettings>>(LAYOUT_KEY) ?? {};
  return {
    readerShare: clampShare(typeof saved.readerShare === "number" ? saved.readerShare : 0.6),
    chatOpen: saved.chatOpen ?? true,
  };
}

export function saveLayout(layout: LayoutSettings): void {
  write(LAYOUT_KEY, layout);
}
